// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Appointment, CarePlan, CodeSystem, CoverageEligibilityResponse, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { EligibilityStatus, SchedulingEligibility } from '@polarissleep/scheduling';
import {
  APPOINTMENT_TYPE_SYSTEM,
  determineSchedulingEligibility,
  mapPatientTriageStatusToTriageStage,
  parseAppointmentTypeCodeSystem,
} from '@polarissleep/scheduling';
import {
  isRpmCarePlan,
  isSelfPayPatient,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  RPM_CARE_PLAN_CATEGORY_CODE,
  RPM_CARE_PLAN_CATEGORY_SYSTEM,
} from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

const eligibilityInFlight = new Map<string, Promise<SchedulingEligibility>>();

/**
 * Hook that checks a specific patient's scheduling eligibility (provider-app).
 *
 * Differs from the patient-app hook by taking `patient: Patient` explicitly
 * instead of reading it from the Medplum profile (the provider-app profile
 * is a Practitioner).
 *
 * Gathers system status, insurance eligibility, triage stage, and appointment
 * types to determine if the patient can schedule and which types are available.
 * @param patient - The patient to check eligibility for
 * @returns Scheduling eligibility, loading state, error, and refresh function
 */
export function useSchedulingEligibility(patient: Patient | undefined): {
  eligibility: SchedulingEligibility | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const [eligibility, setEligibility] = useState<SchedulingEligibility | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const patientId = patient?.id;

  const fetchEligibility = useCallback(async () => {
    if (!patientId) {
      setEligibility(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let request = eligibilityInFlight.get(patientId);
      if (!request) {
        request = (async () => {
          // Fetch all required data in parallel. The Patient is re-read fresh so the
          // triage-status tag reflects the latest provider edit.
          const [coverages, patientResource, codeSystem, eligibilityResponse, bookedAppointments, rpmCarePlans] =
            await Promise.all([
              medplum.searchResources('Coverage', {
                beneficiary: `Patient/${patientId}`,
                status: 'active',
                _count: '1',
              }),
              medplum.readResource('Patient', patientId),
              medplum.searchOne('CodeSystem', { url: APPOINTMENT_TYPE_SYSTEM }),
              medplum.searchOne('CoverageEligibilityResponse', {
                patient: `Patient/${patientId}`,
                _sort: '-created',
              }),
              medplum.searchResources('Appointment', {
                patient: `Patient/${patientId}`,
                _count: '100',
              }),
              medplum.searchResources('CarePlan', {
                subject: `Patient/${patientId}`,
                status: 'active',
                category: `${RPM_CARE_PLAN_CATEGORY_SYSTEM}|${RPM_CARE_PLAN_CATEGORY_CODE}`,
                _count: '100',
                _sort: '-_lastUpdated',
              }),
            ]);

          let eligibilityStatus: EligibilityStatus;
          const typedResponse = eligibilityResponse as CoverageEligibilityResponse | undefined;
          if (typedResponse?.outcome === 'complete') {
            const inforce = typedResponse.insurance?.[0]?.inforce;
            eligibilityStatus = inforce === true ? 'eligible' : 'ineligible';
          } else if (coverages.length > 0) {
            eligibilityStatus = 'eligible';
          } else {
            eligibilityStatus = 'unknown';
          }

          const typedPatient = patientResource as Patient;
          const typedBookedAppointments = bookedAppointments as Appointment[];
          const typedRpmCarePlans = rpmCarePlans as CarePlan[];
          const triageStatusCode = typedPatient.meta?.tag?.find(
            (t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM
          )?.code;
          const triageStage = mapPatientTriageStatusToTriageStage(triageStatusCode);
          const appointmentTypes = codeSystem
            ? parseAppointmentTypeCodeSystem((codeSystem as CodeSystem).concept ?? []).filter(
                (type) => type.schedulable !== false
              )
            : [];

          const existingBookedTypeCodes = typedBookedAppointments
            .filter((appt) => appt.status === 'pending' || appt.status === 'booked')
            .map((appt) => appt.appointmentType?.coding?.[0]?.code)
            .filter((code): code is string => Boolean(code));
          const rpmActive = typedRpmCarePlans.some(
            (carePlan) => carePlan.status === 'active' && isRpmCarePlan(carePlan)
          );

          return determineSchedulingEligibility({
            eligibilityStatus,
            triageStage,
            appointmentTypes,
            existingBookedTypeCodes,
            isClinicianBooking: true,
            isSelfPay: isSelfPayPatient(typedPatient),
            rpmActive,
            allowDuplicateTypes: true,
          });
        })();
        eligibilityInFlight.set(patientId, request);
      }

      const result = await request;
      setEligibility(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to check scheduling eligibility'));
    } finally {
      eligibilityInFlight.delete(patientId);
      setLoading(false);
    }
  }, [medplum, patientId]);

  useEffect(() => {
    fetchEligibility().catch(console.error);
  }, [fetchEligibility]);

  return { eligibility, loading, error, refresh: () => fetchEligibility() };
}
